import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Badge, Button, Form, InputGroup } from 'react-bootstrap';
import './CustomerManagement.css';
import api from '../services/api';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';

/** Sloupce výpisu v pořadí hlavičky — klíče, pod kterými se ukládají šířky. */
const SLOUPCE = ['vyber', 'firma', 'ico', 'stav', 'tagy', 'akce'];

function CustomerManagement({ openTab }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('crm.companies', SLOUPCE);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const itemsPerPage = 100;

  // Fetch companies from API
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getCompanies({
        search: debouncedSearchTerm,
        status: statusFilter,
        page: currentPage,
        limit: itemsPerPage,
        sort: sortColumn,
        order: sortOrder,
      });

      const data = result.data || [];
      const pagination = result.pagination || {};

      // Transform API data to match component expectations
      const transformed = data.map(item => ({
        id: item.id,
        name: item.company_name || '',
        ico: item.ico || '',
        addresses: item.address_count || 0,
        contacts: item.contact_count || 0,
        salespeople: item.salespeople || [],
        manager: null,
        status: item.state === 'active' ? 'active' : 'inactive',
        tags: item.tags || [],
        phone: item.phone || '',
        email: item.email || '',
        web: item.homepage_url || '',
        note: item.note || '',
        lastEventDate: item.last_event_date ? item.last_event_date.split('T')[0] : '',
      }));

      setCustomers(transformed);
      setTotalPages(pagination.totalPages || 1);
      setTotalItems(pagination.total || 0);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      setCustomers([]);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, statusFilter, currentPage, itemsPerPage, sortColumn, sortOrder]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }}></i>;
    return sortOrder === 'asc'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }}></i>
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
  };

  // Debounce search
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
      const timer = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, debouncedSearchTerm]);

  // Pagination helpers
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + customers.length;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <Badge bg="success">Aktivní</Badge>;
    }
    return <Badge bg="secondary">Neaktivní</Badge>;
  };

  const handleAddCustomer = () => {
    openTab('customer-new', 'Nová firma', '/customer/new', 'CustomerForm');
  };

  const handleExportSelected = () => {
    if (selectedCustomers.length === 0) {
      alert('Nejsou vybrány žádné firmy k exportu');
      return;
    }

    const exportData = selectedCustomers.map(id => {
      const customer = customers.find(c => c.id === id);
      if (!customer) return null;
      return {
        'ID': customer.id,
        'Název společnosti': customer.name,
        'IČO': customer.ico,
        'Adresy': customer.addresses,
        'Kontakty': customer.contacts,
        'Obchodníci': customer.salespeople.join(', '),
        'Stav': customer.status === 'active' ? 'Aktivní' : 'Neaktivní',
        'Tagy': customer.tags.join(', '),
        'Datum poslední události': customer.lastEventDate || ''
      };
    }).filter(Boolean);

    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(';'),
      ...exportData.map(row => headers.map(header => `"${row[header]}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `zakaznici_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const toggleAllCustomers = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c.id));
    }
  };

  const handleStatusFilterChange = (e) => {
    const val = e.target.value;
    setStatusFilter(val === 'Aktivní' ? 'active' : val === 'Neaktivní' ? 'inactive' : '');
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    setSortColumn('company_name');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  return (
    <Container fluid className="px-4">
      {/* Header Bar */}
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center">
            <div className="col-md-3">
              <h4 className="mb-0">Zákazník</h4>
            </div>
            <div className="col-md-9 text-end">
              <Button variant="primary" size="sm" onClick={handleAddCustomer}>
                <i className="bi bi-plus-lg"></i> Přidat firmu
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="ms-2"
                onClick={handleExportSelected}
                disabled={selectedCustomers.length === 0}
              >
                <i className="bi bi-download"></i> Export vybraných ({selectedCustomers.length})
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Filters and Table */}
      <div className="table-container">
        <div className="row g-3 align-items-end mb-4">
          <div className="col-md-4">
            <label className="form-label">Hledat</label>
            <InputGroup>
              <span className="input-group-text bg-white border-end-0">
                <i className="bi bi-search"></i>
              </span>
              <Form.Control
                className="border-start-0 search-box"
                placeholder="Název firmy, IČO, kontakt…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-md-3">
            <label className="form-label">Stav</label>
            <Form.Select value={statusFilter === 'active' ? 'Aktivní' : statusFilter === 'inactive' ? 'Neaktivní' : 'Vše'} onChange={handleStatusFilterChange}>
              <option>Vše</option>
              <option>Aktivní</option>
              <option>Neaktivní</option>
            </Form.Select>
          </div>
          <div className="col-md-5">
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" className="flex-grow-1" onClick={handleReset}>
                <i className="bi bi-arrow-counterclockwise"></i> Reset
              </Button>
              <ColumnWidthsReset maUlozene={maUlozene} vychozi={vychozi} />
            </div>
          </div>
        </div>

        {/* Table with loading overlay */}
        <div className="table-loading-wrapper" style={{ position: 'relative' }}>
          {loading && (
            <div className="table-loading-overlay">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Načítání...</span>
              </div>
            </div>
          )}
          <div className="table-responsive grid-wrap" ref={wrapRef} style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease', pointerEvents: loading ? 'none' : 'auto' }}>
            <Table hover className="align-middle grid-resizable" style={tableStyle}>
              {colgroup && (
                <colgroup>
                  {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
              )}
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedCustomers.length === customers.length && customers.length > 0}
                      onChange={toggleAllCustomers}
                    />
                    <span {...resizerProps('vyber')} />
                  </th>
                  <th role="button" onClick={() => handleSort('company_name')}>Firma <SortIcon column="company_name" /><span {...resizerProps('firma')} /></th>
                  <th>IČO<span {...resizerProps('ico')} /></th>
                  <th role="button" onClick={() => handleSort('state')}>Stav <SortIcon column="state" /><span {...resizerProps('stav')} /></th>
                  <th>Tagy<span {...resizerProps('tagy')} /></th>
                  <th>Akce<span {...resizerProps('akce')} /></th>
                </tr>
              </thead>
              <tbody style={{ opacity: isSearching ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                {customers.map(customer => (
                  <tr key={customer.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={() => toggleCustomerSelection(customer.id)}
                      />
                    </td>
                    <td>
                      <div className="company-name">
                        <a href="#" onClick={(e) => { e.preventDefault(); openTab(`customer-${customer.id}`, customer.name, `/customer/${customer.id}`, 'CustomerDetail', { customerId: customer.id }); }} className="text-decoration-none text-dark">
                          {customer.name}
                        </a>
                      </div>
                      <div className="company-details">
                        Adresy: {customer.addresses} · Kontakty: {customer.contacts}
                      </div>
                    </td>
                    <td>{customer.ico}</td>
                    <td>{getStatusBadge(customer.status)}</td>
                    <td>
                      {customer.tags.slice(0, 5).map((tag, idx) => (
                        <span key={idx} className="tag-badge">{tag}</span>
                      ))}
                      {customer.tags.length > 5 && (
                        <span
                          className="tag-badge text-primary"
                          role="button"
                          style={{ cursor: 'pointer', fontStyle: 'italic', background: 'transparent', border: 'none', padding: '0 4px' }}
                          title={customer.tags.slice(5).join(', ')}
                          onClick={() => openTab(`customer-${customer.id}`, customer.name, `/customer/${customer.id}`, 'CustomerDetail', { customerId: customer.id })}
                        >
                          +{customer.tags.length - 5} dalších
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-secondary"
                        className="btn-action"
                        title="Zobrazit detail"
                        onClick={() => openTab(`customer-${customer.id}`, customer.name, `/customer/${customer.id}`, 'CustomerDetail', { customerId: customer.id })}
                      >
                        <i className="bi bi-eye"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <span className="text-muted">
              Zobrazeno: {customers.length > 0 ? startIndex + 1 : 0}–{endIndex} z {totalItems}
            </span>
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>‹</button>
              </li>
              {getPageNumbers().map((page, idx) => (
                <li key={idx} className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                  {page === '...' ? (
                    <span className="page-link">...</span>
                  ) : (
                    <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                  )}
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages || totalPages === 0}>›</button>
              </li>
              <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>»</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </Container>
  );
}

export default CustomerManagement;
